import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TruckIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../ui/LoadingSpinner';

const VehicleMappingModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  mappingData, 
  loading = false 
}) => {
  const { t } = useTranslation();
  const [mappings, setMappings] = useState([]);

  useEffect(() => {
    if (mappingData?.suggestedMappings) {
      // Initialize mappings with suggested matches
      const initialMappings = mappingData.suggestedMappings.map(suggestion => ({
        petroAppPlate: suggestion.petroAppPlate,
        systemVehiclePlate: suggestion.suggestions.length > 0 
          ? suggestion.suggestions[0].vehicle?.plateNumber || suggestion.suggestions[0].vehicle?.id || ''
          : '',
        transactionCount: suggestion.transactionCount,
        suggestions: suggestion.suggestions,
        confirmed: false
      }));
      setMappings(initialMappings);
    }
  }, [mappingData]);

  const handleMappingChange = (index, systemVehiclePlate) => {
    const updatedMappings = [...mappings];
    updatedMappings[index].systemVehiclePlate = systemVehiclePlate;
    updatedMappings[index].confirmed = systemVehiclePlate !== '';
    setMappings(updatedMappings);
  };

  const handleConfirm = () => {
    const confirmedMappings = mappings.map(mapping => ({
      petroAppPlate: mapping.petroAppPlate,
      systemVehiclePlate: mapping.systemVehiclePlate || 'skip',
      transactionCount: mapping.transactionCount
    }));
    onConfirm(confirmedMappings);
  };

  const canProceed = mappings.every(mapping => 
    mapping.systemVehiclePlate !== '' || mapping.confirmed
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <TruckIcon className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {t('fuel.vehicle_mapping_required')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          <div className="mb-6">
            <div className="flex items-center space-x-2 text-amber-600 mb-2">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <span className="font-medium">{t('fuel.mapping_explanation')}</span>
            </div>
            <p className="text-gray-600 text-sm">
              {t('fuel.mapping_description')}
            </p>
          </div>

          <div className="space-y-4">
            {mappings.map((mapping, index) => (
              <div key={mapping.petroAppPlate} className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-100 px-3 py-1 rounded-full">
                      <span className="text-blue-800 font-medium">
                        {mapping.petroAppPlate}
                      </span>
                    </div>
                    <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {mapping.transactionCount} {t('fuel.transactions')}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('fuel.select_system_vehicle')}
                  </label>
                  
                  <select
                    value={mapping.systemVehiclePlate}
                    onChange={(e) => handleMappingChange(index, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  >
                    <option value="">{t('fuel.select_vehicle')}</option>
                    {mapping.suggestions.map((suggestion, suggestionIndex) => (
                      <option 
                        key={suggestionIndex} 
                        value={suggestion.vehicle?.plateNumber || suggestion.vehicle?.id || ''}
                      >
                        {suggestion.vehicle?.plateNumber || suggestion.vehicle?.id} 
                        {suggestion.similarity < 1.0 && (
                          ` (${Math.round(suggestion.similarity * 100)}% ${t('fuel.similarity')})`
                        )}
                      </option>
                    ))}
                    <option value="skip">{t('fuel.skip_this_vehicle')}</option>
                  </select>

                  {mapping.suggestions.length === 0 && (
                    <p className="text-sm text-amber-600">
                      {t('fuel.no_suggestions_found')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {mappings.length === 0 && (
            <div className="text-center py-8">
              <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-600">{t('fuel.all_vehicles_mapped')}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            {mappings.length > 0 && (
              <span>
                {mappings.filter(m => m.confirmed || m.systemVehiclePlate).length} / {mappings.length} {t('fuel.vehicles_mapped')}
              </span>
            )}
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            
            <button
              onClick={handleConfirm}
              disabled={loading || !canProceed}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {loading && <LoadingSpinner size="sm" />}
              <span>{t('fuel.confirm_mappings')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleMappingModal;